import { Location } from '@angular/common';
import { ChangeDetectorRef, Component, HostListener, OnInit, ViewChild } from '@angular/core';
import { MatDrawer } from '@angular/material/sidenav';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { FuseMediaWatcherService } from '@fuse/services/media-watcher';
import { ExposedReadUserDetailsRes } from 'app/interfaces/responses/Exposed/exposedReadUserDetailsRes';
import { LsrauthService } from 'app/services/api/lsrauth/lsrauth.service';
import { TranslationService } from 'app/services/api/translation/translation.service';
import { USER_ROLES } from 'common/constants/user-role.constants';
import { Observable, Subject, combineLatest, filter, takeUntil } from 'rxjs';

@Component({
    selector: 'app-settings',
    templateUrl: './settings.component.html',
    styleUrls: ['./settings.component.scss'],
})
export class SettingsComponent implements OnInit {
    //#region Keypresses and before leave

    @HostListener('window:beforeunload')
    canDeactivate(): Observable<boolean> | boolean {
        try {
            let hasNoFormChanges = false;
            return !hasNoFormChanges;
        } catch (e) {
            return true;
        }
    }

    //#endregion

    //#region Globals

    userDetails: ExposedReadUserDetailsRes = null;
    beforeLeaveMessage = this.translationService.translate('info_leave_form_message');
    panels: any[] = [];
    selectedPanel: string = 'stillingar';
    selectedComponent: string = 'app-settings-users';

    //#endregion

    //#region Init

    /**
     * Constructor
     */
    constructor(
        private _changeDetectorRef: ChangeDetectorRef,
        private _fuseMediaWatcherService: FuseMediaWatcherService,
        private location: Location,
        private route: ActivatedRoute,
        private router: Router,
        private translationService: TranslationService,
        public lsrauthService: LsrauthService
    ) {
        this.lsrauthService.userDetails.subscribe((userDetails) => (this.userDetails = userDetails));
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Lifecycle hooks
    // -----------------------------------------------------------------------------------------------------

    /**
     * On init
     */
    ngOnInit(): void {
        this.initPanel();
    }

    ngAfterViewInit(): void {}

    /**
     * On destroy
     */
    ngOnDestroy(): void {
        // Unsubscribe from all subscriptions
        this._unsubscribeAll.next(null);
        this._unsubscribeAll.complete();
    }

    //#endregion

    //#region FUSE

    private _unsubscribeAll: Subject<any> = new Subject<any>();
    @ViewChild('drawer') drawer: MatDrawer;
    drawerMode: 'over' | 'side' = 'side';
    drawerOpened: boolean = true;

    /**
     * Track by function for ngFor loops
     *
     * @param index
     * @param item
     */
    trackByFn(index: number, item: any): any {
        return item.id || index;
    }

    //#endregion

    //#region Panel

    initPanel() {
        combineLatest([this.lsrauthService.fetchUserDetails(true)]).subscribe(([userProfile]) => {
            // Setup available panels
            this.panels = [
                {
                    id: 'notendastillingar',
                    component: 'app-settings-users',
                    icon: 'heroicons_solid:user-group',
                    title: this.translationService.translate('label_user_settings'),
                    description: this.translationService.translate('info_user_settings'),
                    disabled: true,
                },
            ];

            if (this.lsrauthService.inRole(this.userDetails, USER_ROLES.SERVICEUSER_MANAGEMENT)) {
                this.panels.push({
                    id: 'kerfisnotendastillingar',
                    component: 'app-settings-serviceusers',
                    icon: 'heroicons_solid:building-office-2',
                    title: this.translationService.translate('label_service_user_settings'),
                    description: this.translationService.translate('info_service_user_settings'),
                });
            }

            if (
                this.lsrauthService.inRole(this.userDetails, USER_ROLES.SYSTEM_SETTINGS) ||
                this.lsrauthService.inRole(this.userDetails, USER_ROLES.CONFIGS_SUPERUSER_ROLE) ||
                this.lsrauthService.inRole(this.userDetails, USER_ROLES.EDIT_CONFIGS_ROLE)
            ) {
                this.panels.push({
                    id: 'kerfisstillingar',
                    component: 'app-settings-configs',
                    icon: 'mat_solid:settings',
                    title: this.translationService.translate('label_system_settings'),
                    description: this.translationService.translate('info_system_settings'),
                });
            }
            if (
                this.lsrauthService.inRole(this.userDetails, USER_ROLES.TRANSLATIONS_SUPERUSER_ROLE) ||
                this.lsrauthService.inRole(this.userDetails, USER_ROLES.EDIT_TRANSLATIONS_ROLE)
            ) {
                this.panels.push({
                    id: 'thydingar',
                    component: 'app-settings-translations',
                    icon: 'mat_solid:translate',
                    title: this.translationService.translate('label_translations'),
                    description: this.translationService.translate('info_translations'),
                });
            }

            this.selectedPanel = this.route.snapshot.paramMap.get('panel');
            this.selectedComponent = this.panels.find((x) => x.id == this.route.snapshot.paramMap.get('panel')).component;

            // Subscribe to media changes
            this._fuseMediaWatcherService.onMediaChange$.pipe(takeUntil(this._unsubscribeAll)).subscribe(({ matchingAliases }) => {
                // Set the drawerMode and drawerOpened
                if (matchingAliases.includes('lg')) {
                    this.drawerMode = 'side';
                    this.drawerOpened = true;
                } else {
                    this.drawerMode = 'over';
                    this.drawerOpened = false;
                }

                // Mark for check
                this._changeDetectorRef.markForCheck();
            });

            // Makes sure to allow user to switch tabs from menu panel (username)
            this.router.events.pipe(filter((event: any) => event instanceof NavigationEnd)).subscribe((x) => {
                this.selectedPanel = this.route.snapshot.paramMap.get('panel');
                this.selectedComponent = this.panels.find((x) => x.id == this.route.snapshot.paramMap.get('panel')).component;
            });
        });
    }

    /**
     * Navigate to the panel. Checks if something has been editied within the associated forms.
     *
     * @param panel
     */
    goToPanel(panel: string, component: string): void {
        this.proceedPanel(panel, component);
    }

    proceedPanel(panel: string, component: string): void {
        this.location.go('/vefstillingar/' + panel);
        this.clearTouchForms();
        this.selectedPanel = panel;
        this.selectedComponent = component;

        // Close the drawer on 'over' mode
        if (this.drawerMode === 'over') {
            this.drawer.close();
        }
    }

    // When switching between panels we mark all forms as untouched.
    clearTouchForms() {}

    /**
     * Get the details of the panel
     *
     * @param id
     */
    getPanelInfo(id: string): any {
        return this.panels.find((panel) => panel.id === id);
    }

    getPanelTitle(id: string): any {
        try {
            return this.panels.find((panel) => panel.id === id).title;
        } catch {
            return '';
        }
    }

    getPanelDescription(id: string): any {
        try {
            return this.panels.find((panel) => panel.id === id).description;
        } catch {
            return '';
        }
    }
}
